import React, { useState, useEffect } from 'react';
import { 
  getFinancialReports,
  exportFinancialReport
} from '../../services/api';
import { 
  FiRefreshCw, 
  FiAlertCircle,
  FiCheck,
  FiFilter,
  FiCalendar,
  FiDownload,
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiCreditCard,
  FiUsers,
  FiShield,
  FiPieChart,
  FiBarChart2
} from 'react-icons/fi';
import { TbReceiptTax, TbBuildingBank } from 'react-icons/tb';
import ReportCard from './components/ReportCard';
import ProgressBar from './components/ProgressBar';

function FinancialReports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState('overview');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchFinancialReport();
  }, [dateRange, reportType]);

  const fetchFinancialReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        date_from: dateRange.start,
        date_to: dateRange.end,
        type: reportType
      };

      const response = await getFinancialReports(params);
      
      if (response.success) {
        setReport(response.data);
      } else {
        throw new Error(response.message || 'Failed to load financial report');
      }
    } catch (err) {
      setError(`Failed to load report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      setExporting(true);
      setError('');
      
      const params = {
        date_from: dateRange.start,
        date_to: dateRange.end,
        type: reportType,
        format: format
      };

      const response = await exportFinancialReport(params);
      
      if (response.success) {
        setSuccessMessage(`Report exported successfully as ${format.toUpperCase()}!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Create download link
        let blob, filename, mimeType;
        
        if (format === 'json') {
          blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
          filename = `financial-report-${dateRange.start}-to-${dateRange.end}.json`;
          mimeType = 'application/json';
        } else if (format === 'csv') {
          // Assuming response.data is CSV string
          blob = new Blob([response.data], { type: 'text/csv' });
          filename = `financial-report-${dateRange.start}-to-${dateRange.end}.csv`;
          mimeType = 'text/csv';
        } else if (format === 'pdf') {
          // Assuming response.data is base64 PDF
          const byteCharacters = atob(response.data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: 'application/pdf' });
          filename = `financial-report-${dateRange.start}-to-${dateRange.end}.pdf`;
          mimeType = 'application/pdf';
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
      } else {
        throw new Error(response.message || 'Export failed');
      }
    } catch (err) {
      setError(`Failed to export report: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateMetrics = () => {
    if (!report?.overview) return [];
    
    const { overview } = report;
    
    return [
      {
        title: "Total Revenue",
        value: parseFloat(overview.total_revenue) || 0,
        change: 15.5,
        icon: FiTrendingUp,
        color: "green",
        description: "Total income from all sources"
      },
      {
        title: "Total Withdrawals",
        value: parseFloat(overview.total_withdrawals) || 0,
        change: -8.2,
        icon: FiTrendingDown,
        color: "red",
        description: "Total amount withdrawn by users"
      },
      {
        title: "Platform Fees",
        value: parseFloat(overview.total_platform_fees) || 0,
        change: 12.3,
        icon: TbReceiptTax,
        color: "orange",
        description: "Fees collected by platform"
      },
      {
        title: "Processor Fees",
        value: parseFloat(overview.total_processor_fees) || 0,
        change: 9.7,
        icon: FiCreditCard,
        color: "blue",
        description: "Fees paid to payment processors"
      },
      {
        title: "Boost Revenue",
        value: parseFloat(overview.boost_revenue) || 0,
        change: 25.4,
        icon: FiBarChart2,
        color: "purple",
        description: "Revenue from boost services"
      },
      {
        title: "Active Wallets",
        value: overview.active_wallets || 0,
        change: 5.2,
        icon: FiUsers,
        color: "blue",
        format: "number",
        description: "Number of active user wallets"
      },
      {
        title: "Platform Balance",
        value: parseFloat(overview.total_platform_balance) || 0,
        change: 3.8,
        icon: FiCreditCard,
        color: "green",
        description: "Total balance across all wallets"
      },
      {
        title: "Held Escrow",
        value: parseFloat(overview.held_escrow) || 0,
        change: 18.6,
        icon: FiShield,
        color: "orange",
        description: "Funds held in escrow"
      }
    ];
  };

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Financial Reports</h1>
            <p className="text-gray-600 mt-1">Comprehensive financial analysis and insights</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <button
                onClick={() => handleExport('json')}
                disabled={exporting}
                className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
              >
                <FiDownload className="mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </button>
              {/* Export options dropdown can be added here */}
            </div>
            <button
              onClick={fetchFinancialReport}
              className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheck className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Report Period & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                >
                  <option value="overview">Overview</option>
                  <option value="detailed">Detailed</option>
                  <option value="revenue">Revenue Analysis</option>
                  <option value="expenses">Expense Analysis</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                />
              </div>
            </div>
          </div>
          
          <div className="flex flex-col items-end">
            <button
              onClick={fetchFinancialReport}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center"
            >
              <FiFilter className="mr-2" />
              Apply Filters
            </button>
            
            {report?.period && (
              <div className="text-sm text-gray-500 mt-2">
                Report Period: {formatDate(report.period.from)} - {formatDate(report.period.to)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profit/Loss Summary */}
      {report?.overview && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-[#0f0e0e]">Profit & Loss Summary</h2>
            <div className={`px-4 py-2 rounded-lg font-medium ${
              parseFloat(report.overview.net_profit || 0) >= 0 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {parseFloat(report.overview.net_profit || 0) >= 0 ? 'Profit' : 'Loss'}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Financial Metrics */}
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-green-600 mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(report.overview.total_revenue)}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-sm text-red-600 mb-1">Total Withdrawals</p>
                  <p className="text-2xl font-bold text-red-700">
                    {formatCurrency(report.overview.total_withdrawals)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-4">Net Profit Analysis</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Net Profit</span>
                      <span className={`font-bold ${
                        parseFloat(report.overview.net_profit || 0) >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {formatCurrency(report.overview.net_profit)}
                      </span>
                    </div>
                    <ProgressBar 
                      value={Math.abs(parseFloat(report.overview.net_profit || 0))}
                      max={Math.max(
                        Math.abs(parseFloat(report.overview.net_profit || 0)),
                        parseFloat(report.overview.total_revenue || 1)
                      )}
                      color={parseFloat(report.overview.net_profit || 0) >= 0 ? '#10b981' : '#ef4444'}
                      showPercentage={false}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Profit Margin</span>
                      <span className={`font-bold ${
                        parseFloat(report.overview.profit_margin || 0) >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {parseFloat(report.overview.profit_margin || 0).toFixed(2)}%
                      </span>
                    </div>
                    <ProgressBar 
                      value={Math.abs(parseFloat(report.overview.profit_margin || 0))}
                      max={100}
                      color={parseFloat(report.overview.profit_margin || 0) >= 0 ? '#10b981' : '#ef4444'}
                      showPercentage={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Breakdown Chart */}
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-4">Revenue Breakdown</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Platform Fees</span>
                    <span className="font-medium">{formatCurrency(report.overview.total_platform_fees)}</span>
                  </div>
                  <ProgressBar 
                    value={parseFloat(report.overview.total_platform_fees || 0)}
                    max={parseFloat(report.overview.total_revenue || 1)}
                    color="#ff660e"
                    showPercentage={true}
                  />
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Processor Fees</span>
                    <span className="font-medium">{formatCurrency(report.overview.total_processor_fees)}</span>
                  </div>
                  <ProgressBar 
                    value={parseFloat(report.overview.total_processor_fees || 0)}
                    max={parseFloat(report.overview.total_revenue || 1)}
                    color="#3b82f6"
                    showPercentage={true}
                  />
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Boost Revenue</span>
                    <span className="font-medium">{formatCurrency(report.overview.boost_revenue)}</span>
                  </div>
                  <ProgressBar 
                    value={parseFloat(report.overview.boost_revenue || 0)}
                    max={parseFloat(report.overview.total_revenue || 1)}
                    color="#8b5cf6"
                    showPercentage={true}
                  />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Assets Overview</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">Platform Balance</p>
                    <p className="text-lg font-bold text-blue-700">
                      {formatCurrency(report.overview.total_platform_balance)}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-600 mb-1">Held Escrow</p>
                    <p className="text-lg font-bold text-orange-700">
                      {formatCurrency(report.overview.held_escrow)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0f0e0e]">Key Financial Metrics</h2>
          <span className="text-sm text-gray-500">
            {report?.generated_at && `Generated: ${new Date(report.generated_at).toLocaleString()}`}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {calculateMetrics().map((metric, index) => (
            <ReportCard
              key={index}
              title={metric.title}
              value={metric.value}
              change={metric.change}
              icon={metric.icon}
              color={metric.color}
              format={metric.format}
              description={metric.description}
              isLoading={loading && !report}
            />
          ))}
        </div>
      </div>

      {/* Financial Ratios */}
      {report?.overview && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-[#0f0e0e] mb-6">Financial Ratios & Analysis</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <TbBuildingBank className="text-blue-600 text-3xl mx-auto mb-3" />
              <p className="text-sm text-blue-700 mb-1">Withdrawal to Revenue Ratio</p>
              <p className="text-2xl font-bold text-blue-800">
                {((parseFloat(report.overview.total_withdrawals || 0) / 
                  parseFloat(report.overview.total_revenue || 1)) * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-blue-600 mt-2">Percentage of revenue withdrawn</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
              <FiPieChart className="text-green-600 text-3xl mx-auto mb-3" />
              <p className="text-sm text-green-700 mb-1">Platform Fee Efficiency</p>
              <p className="text-2xl font-bold text-green-800">
                {((parseFloat(report.overview.total_platform_fees || 0) / 
                  parseFloat(report.overview.total_revenue || 1)) * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-green-600 mt-2">Platform fees as % of revenue</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
              <FiDollarSign className="text-purple-600 text-3xl mx-auto mb-3" />
              <p className="text-sm text-purple-700 mb-1">Average Wallet Balance</p>
              <p className="text-2xl font-bold text-purple-800">
                {formatCurrency(
                  parseFloat(report.overview.total_platform_balance || 0) / 
                  (report.overview.active_wallets || 1)
                )}
              </p>
              <p className="text-xs text-purple-600 mt-2">Per active wallet</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-semibold text-gray-700 mb-4">Recommendations</h3>
            <div className="space-y-3">
              {parseFloat(report.overview.profit_margin || 0) < 0 && (
                <div className="flex items-start p-3 bg-yellow-50 rounded-lg">
                  <FiAlertCircle className="text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Operating at a Loss</p>
                    <p className="text-xs text-yellow-700">
                      Consider optimizing fee structure or reducing operational costs to achieve profitability.
                    </p>
                  </div>
                </div>
              )}
              
              {parseFloat(report.overview.total_withdrawals || 0) > parseFloat(report.overview.total_revenue || 0) && (
                <div className="flex items-start p-3 bg-red-50 rounded-lg">
                  <FiAlertCircle className="text-red-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-red-800">High Withdrawal Volume</p>
                    <p className="text-xs text-red-700">
                      Withdrawals exceed revenue. Review withdrawal policies and incentives to retain platform liquidity.
                    </p>
                  </div>
                </div>
              )}
              
              {parseFloat(report.overview.held_escrow || 0) > 0 && (
                <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                  <FiAlertCircle className="text-blue-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Active Escrow Funds</p>
                    <p className="text-xs text-blue-700">
                      {formatCurrency(report.overview.held_escrow)} held in escrow. Ensure timely disbursement upon transaction completion.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Options */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-[#0f0e0e] mb-4">Export Options</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleExport('json')}
            disabled={exporting}
            className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 font-medium flex items-center disabled:opacity-50"
          >
            <FiDownload className="mr-2" />
            Export as JSON
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 font-medium flex items-center disabled:opacity-50"
          >
            <FiDownload className="mr-2" />
            Export as CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 font-medium flex items-center disabled:opacity-50"
          >
            <FiDownload className="mr-2" />
            Export as PDF
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mt-3">
          Exports include all report data for the selected period in the chosen format.
        </p>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default FinancialReports;